import React, { useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import consoleApi, { errorMessage, saveSession } from "./consoleApi";

const STAFF_DOMAIN = "africacode.academy";

const INPUT =
  "w-full rounded-xl border border-slate-300 bg-white px-3.5 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-4 focus:ring-indigo-500/15";
const BUTTON =
  "w-full rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-indigo-600/20 transition hover:from-indigo-700 hover:to-violet-700 disabled:opacity-50 disabled:shadow-none";

export default function ConsoleLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState("email"); // email | code
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const codeRef = useRef(null);

  const trimmedEmail = email.trim().toLowerCase();
  const wrongDomain = trimmedEmail.includes("@") && trimmedEmail.split("@")[1] && !trimmedEmail.endsWith(`@${STAFF_DOMAIN}`);

  const sendCode = async (e) => {
    e?.preventDefault();
    if (!trimmedEmail || wrongDomain || busy) return;
    setBusy(true);
    setError(null);
    try {
      await consoleApi.post("/auth/request-code", { email: trimmedEmail });
      setStep("code");
      setCode("");
      setInfo(`We sent a 6-digit code to ${trimmedEmail}. It expires in 10 minutes.`);
      setTimeout(() => codeRef.current?.focus(), 0);
    } catch (err) {
      setError(errorMessage(err, "Couldn't send a code. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    const digits = code.replace(/\D/g, "");
    if (digits.length !== 6 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await consoleApi.post("/auth/verify", { email: trimmedEmail, code: digits });
      saveSession(res.data.token, res.data.email);
      // Back to the page they were sent to (e.g. a tenant linked from the signup email)
      const from = location.state?.from;
      navigate(typeof from === "string" && from.startsWith("/") && from !== "/login" ? from : "/", { replace: true });
    } catch (err) {
      setError(errorMessage(err, "Couldn't sign you in. Please try again."));
      setCode("");
      codeRef.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-white">
      {/* Brand panel (desktop) */}
      <div className="relative hidden w-[44%] overflow-hidden bg-[#0d1124] lg:flex lg:flex-col lg:justify-between lg:p-12">
        <div className="pointer-events-none absolute -left-24 -top-24 h-96 w-96 rounded-full bg-indigo-600/30 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 right-0 h-96 w-96 rounded-full bg-violet-600/20 blur-3xl" />
        <div className="relative flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-400 to-violet-600 font-extrabold text-white">R</span>
          <span className="text-base font-bold text-white">RiskFlow</span>
        </div>
        <div className="relative max-w-md">
          <h2 className="text-3xl font-bold leading-tight tracking-tight text-white">Every tenant, in one place.</h2>
          <p className="mt-3 text-base text-slate-400">
            Review new signups, see how each organisation uses RiskFlow, and step in when something needs attention.
          </p>
        </div>
        <p className="relative text-xs text-slate-500">Africa Code Academy · internal use only</p>
      </div>

      {/* Form */}
      <div className="flex flex-1 items-center justify-center bg-[#f5f6fa] px-4 py-12 lg:bg-white">
      <div className="w-full max-w-sm">
        <p className="text-xs font-bold uppercase tracking-widest text-indigo-600">RiskFlow staff console</p>
        <h1 className="mt-2 text-2xl font-bold tracking-tight text-slate-900">{step === "email" ? "Sign in" : "Check your email"}</h1>
        <p className="mt-2 text-sm text-slate-600">
          {step === "email"
            ? <>For Africa Code Academy staff. Use your @{STAFF_DOMAIN} email and we'll send you a code.</>
            : <>Enter the 6-digit code we sent to <span className="font-semibold text-slate-900">{trimmedEmail}</span>.</>}
        </p>

        <div className="mt-8">
          {step === "email" ? (
            <form onSubmit={sendCode} className="space-y-4">
              <div>
                <label htmlFor="console-email" className="mb-1.5 block text-sm font-medium text-slate-700">Work email</label>
                <input
                  id="console-email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(null); }}
                  placeholder={`you@${STAFF_DOMAIN}`}
                  className={INPUT}
                />
                {wrongDomain && (
                  <p className="mt-1.5 text-sm text-red-600">Only @{STAFF_DOMAIN} addresses can sign in.</p>
                )}
              </div>
              <button type="submit" disabled={!trimmedEmail || wrongDomain || busy} className={BUTTON}>
                {busy ? "Sending…" : "Email me a code"}
              </button>
            </form>
          ) : (
            <form onSubmit={verify} className="space-y-4">
              {info && <p className="sr-only" aria-live="polite">{info}</p>}
              <div>
                <label htmlFor="console-code" className="mb-1.5 block text-sm font-medium text-slate-700">6-digit code</label>
                <input
                  id="console-code"
                  ref={codeRef}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={7}
                  value={code}
                  onChange={(e) => { setCode(e.target.value.replace(/[^\d ]/g, "")); setError(null); }}
                  placeholder="123456"
                  className={`${INPUT} text-center font-mono text-lg tracking-[0.4em]`}
                />
              </div>
              <button type="submit" disabled={code.replace(/\D/g, "").length !== 6 || busy} className={BUTTON}>
                {busy ? "Checking…" : "Sign in"}
              </button>
              <div className="flex justify-between text-sm">
                <button
                  type="button"
                  onClick={() => { setStep("email"); setError(null); setInfo(null); }}
                  className="font-medium text-slate-600 hover:text-slate-900"
                >
                  Use a different email
                </button>
                <button
                  type="button"
                  onClick={sendCode}
                  disabled={busy}
                  className="font-medium text-indigo-700 hover:text-indigo-900 disabled:opacity-50"
                >
                  Send a new code
                </button>
              </div>
            </form>
          )}
          {error && <p className="mt-4 rounded-xl bg-red-50 px-3.5 py-2.5 text-sm text-red-700" role="alert">{error}</p>}
        </div>
      </div>
      </div>
    </div>
  );
}
